"use client";

import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ArrowUpDown, EyeOff, Search } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import { InlineTimeRangeControls } from "@/components/globals";
import { EnterprisePageShell } from "@/components/layout/enterprise-page-shell";
import { HeaderDeviceSelect } from "@/components/layout/header-device-select";
import { StatusIndicator } from "@/components/ui/status-indicator";
import { Button, Card, Modal } from "@/components/ui-system";
import { ThemedMultiSelect } from "@/components/ui-system/multi-select";
import { ThemedSelect } from "@/components/ui-system/select";
import { getDeviceById, getDevices } from "@/lib/api/endpoints/devices";
import { exportTelemetry, getTelemetryPage } from "@/lib/api/endpoints/telemetry";
import type { DeviceChannelDto, TelemetryAggregatePointDto, TelemetryDto } from "@/lib/api/types";
import {
  deviceActivityModelFromApi,
  formatDeviceOperationalStatusLabel,
  operationalStatusToIndicatorVariant,
} from "@/lib/devices/device-state-model";
import { readDeviceDisableState } from "@/lib/devices/device-disable-state";
import { useElementWidth } from "@/lib/hooks/use-element-width";
import { cn } from "@/lib/utils/cn";
import { useAuthStore } from "@/modules/auth/store";
import { TelemetryUPlotChart } from "@/modules/telemetry/components/telemetry-uplot-chart";
import { useTelemetryAggregates } from "@/modules/telemetry/hooks";
import { entitlementBlockedMessage } from "@/modules/telemetry/entitlement-ui";
import {
  channelLabelMap,
  formatTelemetryValue,
  summarizeMetric,
} from "@/modules/telemetry/page-utils";
import {
  type ChannelThresholds,
  getChannelThresholds,
  telemetrySeverityTextClass,
  telemetryThresholdSeverity,
} from "@/modules/telemetry/thresholds";
import { useReportingDeviceStore } from "@/store/reporting-device";
import { useTimeRangeStore } from "@/store/time-range";
import { useWorkspaceChromeStore } from "@/store/workspace-chrome";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const EXPLORER_DATE_COLUMN_WIDTH = "13rem";
const AGGREGATOR_LABELS = {
  avg: "avg",
  sum: "sum",
  min: "min",
  max: "max",
  last: "last",
  count: "count",
} as const;

type AggregatorKey = keyof typeof AGGREGATOR_LABELS;
type ExplorerViewMode = "overlay" | "multiples" | "heatmap" | "histogram";
type ExplorerExportFormat = "csv" | "pdf";
type ExplorerExportScope = "all" | "selected";
type ExplorerExportOrder = "desc" | "asc";
type ExplorerTableSortKey = "time" | string;

const EXPLORER_VIEW_OPTIONS: { value: ExplorerViewMode; label: string }[] = [
  { value: "overlay", label: "Overlay" },
  { value: "multiples", label: "Small multiples" },
  { value: "heatmap", label: "Heatmap" },
  { value: "histogram", label: "Histogram" },
];

function formatExplorerDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = String(date.getFullYear()).slice(-2);
  const time = date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });

  return `${day}/${month}/${year}, ${time}`;
}

function coerceTelemetryNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function metricValues(points: TelemetryAggregatePointDto[], key: string): number[] {
  return points
    .map((point) => point.values[key])
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value));
}

function normalizeExplorerPoints(points: TelemetryAggregatePointDto[], keys: string[]): TelemetryAggregatePointDto[] {
  const ranges = Object.fromEntries(
    keys.map((key) => {
      const values = metricValues(points, key);
      return [key, { min: values.length ? Math.min(...values) : 0, max: values.length ? Math.max(...values) : 0 }];
    }),
  ) as Record<string, { min: number; max: number }>;

  return points.map((point) => ({
    t: point.t,
    values: Object.fromEntries(
      keys.map((key) => {
        const value = point.values[key];
        const range = ranges[key];
        if (typeof value !== "number" || !Number.isFinite(value) || !range || range.max <= range.min) {
          return [key, null];
        }
        return [key, ((value - range.min) / (range.max - range.min)) * 100];
      }),
    ),
  }));
}

function buildExplorerHistogram(points: TelemetryAggregatePointDto[], key: string, maxBins = 10) {
  const values = metricValues(points, key);
  if (values.length === 0) return [];
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (max <= min) return [{ min, max, count: values.length }];
  const binCount = Math.max(3, Math.min(maxBins, Math.ceil(Math.sqrt(values.length))));
  const size = (max - min) / binCount;
  const bins = Array.from({ length: binCount }, (_, index) => ({
    min: min + index * size,
    max: index === binCount - 1 ? max : min + (index + 1) * size,
    count: 0,
  }));
  values.forEach((value) => {
    bins[Math.min(binCount - 1, Math.floor((value - min) / size))]!.count += 1;
  });
  return bins;
}

function sampleExplorerPoints(points: TelemetryAggregatePointDto[], maxPoints = 96): TelemetryAggregatePointDto[] {
  if (points.length <= maxPoints) return points;
  const step = Math.ceil(points.length / maxPoints);
  return points.filter((_, index) => index % step === 0).slice(0, maxPoints);
}

function heatmapCellColor(value: number | null | undefined, min: number, max: number): string {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "transparent";
  }
  if (max <= min) {
    return "color-mix(in srgb, var(--accent) 45%, transparent)";
  }
  const ratio = Math.max(0, Math.min(1, (value - min) / (max - min)));
  const strength = 16 + ratio * 76;
  return `color-mix(in srgb, var(--accent) ${strength.toFixed(0)}%, var(--bg-surface-muted))`;
}

function ExplorerMiniChart({
  deviceId,
  metricKey,
  label,
  unit,
  points,
  dataRevision,
}: {
  deviceId: string;
  metricKey: string;
  label: string;
  unit: string;
  points: TelemetryAggregatePointDto[];
  dataRevision: number;
}) {
  const chart = useElementWidth<HTMLDivElement>(260);
  const summary = summarizeMetric(points, metricKey);
  return (
    <div className="min-w-0 rounded-card border border-card-border bg-surface-secondary p-ds-3 shadow-raised">
      <div className="mb-ds-2 flex items-start justify-between gap-ds-2">
        <div className="min-w-0">
          <p className="truncate text-caption font-semibold text-foreground">{label}</p>
          <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">{unit || "value"}</p>
        </div>
        <p className="shrink-0 text-caption font-semibold tabular-nums text-foreground">
          {summary.latest != null ? formatTelemetryValue(summary.latest) : "--"}
        </p>
      </div>
      <div ref={chart.ref} className="min-w-0">
        <TelemetryUPlotChart
          width={chart.width}
          height={180}
          deviceId={deviceId}
          metricKeys={[metricKey]}
          seriesLabels={{ [metricKey]: label }}
          aggregatePoints={points}
          dataRevision={dataRevision}
          liveEnabled={false}
          liveWindowPoints={1024}
        />
      </div>
    </div>
  );
}

function ExplorerLatestReadingCard({
  channel,
  value,
  thresholds,
  range,
}: {
  channel: DeviceChannelDto;
  value: unknown;
  thresholds: ChannelThresholds | null;
  range: { min: number | null; max: number | null };
}) {
  const severity = telemetryThresholdSeverity(value, thresholds);
  const hasValue = value !== null && value !== undefined && value !== "";
  const hasThresholdConfiguration = Boolean(
    thresholds && (
      thresholds.min !== null ||
      thresholds.max !== null ||
      thresholds.warningLow !== null ||
      thresholds.warningHigh !== null
    ),
  );
  const displayValue = typeof value === "boolean" ? (value ? "True" : "False") : hasValue ? formatTelemetryValue(value) : "--";

  return (
    <div
      className={cn(
        "min-w-[11rem] shrink-0 rounded-card border px-ds-3 py-ds-4 shadow-card",
        severity === "critical"
          ? "border-status-error-border bg-status-error-surface"
          : severity === "warning"
            ? "border-status-warning-border bg-status-warning-surface"
            : hasThresholdConfiguration && hasValue
              ? "border-status-success-border bg-status-success-surface"
              : "border-card-border bg-surface-secondary",
      )}
    >
      <p className="truncate text-micro font-semibold uppercase tracking-[0.12em] text-muted">
        {channel.label || channel.key}
      </p>
      <p
        className={cn(
          "mt-1 text-base font-semibold leading-tight",
          severity === "critical"
            ? "text-error"
            : severity === "warning"
              ? "text-warning"
              : hasThresholdConfiguration && hasValue
                ? "text-success"
                : "text-foreground",
        )}
      >
        {displayValue}
        {channel.unit ? <span className="ml-1 text-caption font-medium">{channel.unit}</span> : null}
      </p>
      <div className="mt-2 flex items-center gap-3 text-micro font-semibold tabular-nums">
        <span className="inline-flex items-center gap-1 text-info">
          <ArrowDown className="h-3 w-3" aria-hidden />
          {range.min != null ? formatTelemetryValue(range.min) : "--"}
        </span>
        <span className="inline-flex items-center gap-1 text-error">
          <ArrowUp className="h-3 w-3" aria-hidden />
          {range.max != null ? formatTelemetryValue(range.max) : "--"}
        </span>
      </div>
    </div>
  );
}

function ExplorerHistogramCard({
  channel,
  label,
  points,
}: {
  channel: DeviceChannelDto;
  label: string;
  points: TelemetryAggregatePointDto[];
}) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const bins = useMemo(() => buildExplorerHistogram(points, channel.key), [channel.key, points]);
  const values = useMemo(() => metricValues(points, channel.key), [channel.key, points]);
  const maxCount = Math.max(1, ...bins.map((bin) => bin.count));
  const activeBin = activeIndex == null ? null : bins[activeIndex] ?? null;
  const totalCount = bins.reduce((sum, bin) => sum + bin.count, 0);
  const valueMin = values.length > 0 ? Math.min(...values) : null;
  const valueMax = values.length > 0 ? Math.max(...values) : null;
  const readoutMin = activeBin?.min ?? valueMin;
  const readoutMax = activeBin?.max ?? valueMax;
  const readoutCount = activeBin?.count ?? totalCount;

  return (
    <div className="rounded-card border border-card-border bg-surface-secondary p-ds-3 shadow-raised">
      <div className="mb-ds-3 flex items-start justify-between gap-ds-2">
        <div className="min-w-0">
          <p className="truncate text-caption font-semibold text-foreground">{label}</p>
          <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">{channel.unit || "value distribution"}</p>
        </div>
        <p className="text-caption text-muted">{totalCount} samples</p>
      </div>
      <div
        className="flex h-36 items-end gap-1 rounded-control border border-card-border bg-surface-inset px-ds-3 py-ds-3"
        onMouseLeave={() => setActiveIndex(null)}
      >
        {bins.length === 0 ? (
          <div className="flex flex-1 items-center justify-center text-caption text-muted">No numeric samples</div>
        ) : (
          bins.map((bin, index) => {
            const isActive = activeIndex === index;
            return (
              <button
                key={`${channel.key}-${index}`}
                type="button"
                className="min-w-[0.35rem] flex-1 rounded-t-md border border-primary/20 outline-none transition-[background-color,box-shadow] focus-visible:ring-2 focus-visible:ring-ring"
                style={{
                  height: `${Math.max(4, (bin.count / maxCount) * 100)}%`,
                  backgroundColor:
                    bin.count > 0
                      ? isActive
                        ? "var(--accent)"
                        : "color-mix(in srgb, var(--accent) 76%, transparent)"
                      : "color-mix(in srgb, var(--bg-surface-muted) 65%, transparent)",
                  boxShadow: isActive ? "0 0 0 2px color-mix(in srgb, var(--accent) 22%, transparent)" : undefined,
                }}
                title={`${formatTelemetryValue(bin.min)} - ${formatTelemetryValue(bin.max)}${channel.unit ? ` ${channel.unit}` : ""}: ${bin.count} samples`}
                onMouseEnter={() => setActiveIndex(index)}
                onFocus={() => setActiveIndex(index)}
              />
            );
          })
        )}
      </div>
      <div className="mt-ds-3 rounded-control border border-card-border bg-surface-primary px-ds-3 py-ds-2">
        <div className="flex flex-wrap items-center justify-between gap-ds-2 text-caption">
          <span className="font-semibold text-foreground">
            {readoutMin != null && readoutMax != null
              ? `${formatTelemetryValue(readoutMin)} - ${formatTelemetryValue(readoutMax)}${channel.unit ? ` ${channel.unit}` : ""}`
              : "No range"}
          </span>
          <span className="text-muted">{readoutCount} sample{readoutCount === 1 ? "" : "s"}</span>
        </div>
        <div className="mt-ds-1 text-caption text-muted">
          {activeBin ? "Selected distribution bucket." : "All histogram buckets in the current range."}
        </div>
      </div>
    </div>
  );
}

export function DataExplorerPage() {
  const tenantId = useAuthStore((s) => s.tenantId);
  const user = useAuthStore((s) => s.user);
  const setWorkspaceChrome = useWorkspaceChromeStore((s) => s.setWorkspaceChrome);
  const start = useTimeRangeStore((s) => s.start);
  const end = useTimeRangeStore((s) => s.end);
  const deviceId = useReportingDeviceStore((s) => s.selectedDeviceId);
  const setDeviceId = useReportingDeviceStore((s) => s.setSelectedDeviceId);
  const [searchParams] = useSearchParams();
  const initialDeviceId = searchParams.get("device") ?? "";
  const consumedInitialDeviceIdRef = useRef(false);
  const [rowSearch, setRowSearch] = useState("");
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>(25);
  const [page, setPage] = useState(1);
  const [visibleChannels, setVisibleChannels] = useState<string[]>([]);
  const [exportDropdownValue, setExportDropdownValue] = useState("");
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<ExplorerExportFormat | null>(null);
  const [exportScope, setExportScope] = useState<ExplorerExportScope>("all");
  const [exportOrder, setExportOrder] = useState<ExplorerExportOrder>("desc");
  const [exportChannelKeys, setExportChannelKeys] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<ExplorerTableSortKey>("time");
  const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");
  const [viewMode, setViewMode] = useState<ExplorerViewMode>("overlay");
  const [showGraph, setShowGraph] = useState(true);
  const [normalizeSeries, setNormalizeSeries] = useState(false);
  const allWorkspaces = user?.role === "super_admin" || user?.accountType === "platform";

  const devicesQuery = useQuery({
    queryKey: ["explorer", "devices", allWorkspaces ? "all" : tenantId],
    queryFn: () => getDevices(allWorkspaces ? null : tenantId ?? null, allWorkspaces ? "all" : null, "explorer"),
    enabled: allWorkspaces || Boolean(tenantId),
    staleTime: 30_000,
  });

  const devices = useMemo(() => devicesQuery.data ?? [], [devicesQuery.data]);

  useLayoutEffect(() => {
    setWorkspaceChrome({
      headerAction: (
        <HeaderDeviceSelect
          devices={devices}
          value={deviceId}
          onChange={setDeviceId}
          loading={devicesQuery.isLoading}
          disabled={!allWorkspaces && !tenantId}
        />
      ),
    });
    return () => setWorkspaceChrome({ headerAction: null });
  }, [allWorkspaces, deviceId, devices, devicesQuery.isLoading, setWorkspaceChrome, tenantId]);

  useEffect(() => {
    if (initialDeviceId && !consumedInitialDeviceIdRef.current) {
      consumedInitialDeviceIdRef.current = true;
      setDeviceId(initialDeviceId);
      return;
    }
    if (devicesQuery.isLoading) {
      return;
    }
    if (devices.length === 0) {
      if (deviceId) {
        setDeviceId("");
      }
      return;
    }
    if (!deviceId && devices[0]?.id) {
      setDeviceId(devices[0].id);
      return;
    }
    const exists = devices.some((device) => device.id === deviceId);
    if (deviceId && !exists && !initialDeviceId) {
      setDeviceId(devices[0]?.id ?? "");
    }
  }, [deviceId, devices, devicesQuery.isLoading, initialDeviceId, setDeviceId]);

  const deviceQuery = useQuery({
    queryKey: ["explorer", "device", deviceId, allWorkspaces ? "all" : "tenant"],
    queryFn: () => getDeviceById(deviceId, allWorkspaces ? "all" : null, "explorer"),
    enabled: Boolean(deviceId),
    staleTime: 20_000,
  });
  const disableState = useMemo(() => readDeviceDisableState(deviceQuery.data), [deviceQuery.data]);
  const disableCutoff = disableState?.disabledAt ?? null;
  const disableCutoffKey = disableCutoff?.toISOString() ?? "none";

  const allChannels = useMemo(
    () => deviceQuery.data?.channels ?? [],
    [deviceQuery.data?.channels],
  );
  const numericChannels = useMemo(
    () => allChannels.filter((channel) => channel.data_type === "number"),
    [allChannels],
  );
  const channelLabels = useMemo(() => channelLabelMap(allChannels), [allChannels]);
  const channelThresholds = useMemo(
    () => Object.fromEntries(numericChannels.map((channel) => [channel.key, getChannelThresholds(deviceQuery.data, channel)])),
    [numericChannels, deviceQuery.data],
  );

  useEffect(() => {
    if (numericChannels.length === 0) {
      setVisibleChannels([]);
      setExportChannelKeys(allChannels.map((channel) => channel.key));
      if (sortBy !== "time") {
        setSortBy("time");
        setSortOrder("desc");
      }
      return;
    }
    setVisibleChannels((current) => {
      const valid = current.filter((key) => numericChannels.some((channel) => channel.key === key));
      return valid.length > 0 ? valid : numericChannels.map((channel) => channel.key);
    });
    setExportChannelKeys((current) => {
      const valid = current.filter((key) => allChannels.some((channel) => channel.key === key));
      return valid.length > 0 ? valid : allChannels.map((channel) => channel.key);
    });
    if (sortBy !== "time" && !allChannels.some((channel) => channel.key === sortBy)) {
      setSortBy("time");
      setSortOrder("desc");
    }
  }, [allChannels, numericChannels, sortBy]);

  const effectiveEnd = disableCutoff && disableCutoff < end ? disableCutoff : end;
  const rowOffset = (page - 1) * pageSize;

  const rowsQuery = useQuery({
    queryKey: [
      "explorer",
      "rows-page",
      deviceId,
      start.toISOString(),
      effectiveEnd.toISOString(),
      allWorkspaces ? "all" : "tenant",
      rowSearch.trim(),
      page,
      pageSize,
      sortBy,
      sortOrder,
      disableCutoffKey,
    ],
    queryFn: () =>
      getTelemetryPage({
        deviceId,
        timeRange: { start, end: effectiveEnd },
        workspaceScope: allWorkspaces ? "all" : null,
        moduleKey: "explorer",
        limit: pageSize,
        offset: rowOffset,
        sortBy,
        order: sortOrder,
        query: rowSearch.trim() || null,
      }),
    enabled: Boolean(deviceId),
    staleTime: 20_000,
  });

  const rowsPage = rowsQuery.data;
  const totalRows = rowsPage?.total ?? 0;
  const pageRows = rowsPage?.items ?? [];

  const latestReadingQuery = useQuery({
    queryKey: [
      "explorer",
      "latest-reading",
      deviceId,
      start.toISOString(),
      effectiveEnd.toISOString(),
      allWorkspaces ? "all" : "tenant",
      disableCutoffKey,
    ],
    queryFn: () =>
      getTelemetryPage({
        deviceId,
        timeRange: { start, end: effectiveEnd },
        workspaceScope: allWorkspaces ? "all" : null,
        moduleKey: "explorer",
        limit: 1,
        offset: 0,
        sortBy: "time",
        order: "desc",
        query: null,
      }),
    enabled: Boolean(deviceId),
    staleTime: 0,
    refetchInterval: 5_000,
  });
  const latestReading = latestReadingQuery.data?.items[0] ?? null;

  useEffect(() => {
    setPage(1);
  }, [deviceId, start, end, rowSearch, pageSize, sortBy, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const pageSummary = `Showing ${totalRows === 0 ? 0 : pageStart + 1} to ${Math.min(pageStart + pageSize, totalRows)} of ${totalRows}`;

  useEffect(() => {
    if (!rowsPage) {
      return;
    }
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, rowsPage, totalPages]);
  const pageAggregatorSummaries = useMemo(
    () =>
      Object.fromEntries(
        numericChannels.map((channel) => {
          const aggregators = normalizeAggregators(channel.aggregation);
          const values = computeChannelAggregators(pageRows, channel.key, aggregators);
          const summary = aggregators
            .map((aggregator) => {
              const value = values[aggregator];
              return `${AGGREGATOR_LABELS[aggregator]}: ${value == null ? "--" : formatValueWithUnit(value, channel.unit, aggregator)}`;
            })
            .join(", ");
          return [channel.key, summary];
        }),
      ) as Record<string, string>,
    [numericChannels, pageRows],
  );
  const frozenPaneRef = useRef<HTMLDivElement | null>(null);
  const scrollPaneRef = useRef<HTMLDivElement | null>(null);
  const leftRowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  const rightRowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});

  const syncExplorerTableRowHeights = useCallback(() => {
    const rowKeys = ["header-main", "header-aggregators", ...pageRows.map((_, index) => `row-${index}`)];

    rowKeys.forEach((key) => {
      const leftRow = leftRowRefs.current[key];
      const rightRow = rightRowRefs.current[key];
      if (leftRow) leftRow.style.height = "";
      if (rightRow) rightRow.style.height = "";
    });

    rowKeys.forEach((key) => {
      const leftRow = leftRowRefs.current[key];
      const rightRow = rightRowRefs.current[key];
      if (!leftRow || !rightRow) return;

      const height = Math.max(leftRow.offsetHeight, rightRow.offsetHeight);
      leftRow.style.height = `${height}px`;
      rightRow.style.height = `${height}px`;
    });
  }, [pageRows]);

  useLayoutEffect(() => {
    const frame = window.requestAnimationFrame(syncExplorerTableRowHeights);
    const handleResize = () => syncExplorerTableRowHeights();

    window.addEventListener("resize", handleResize);

    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(handleResize);
      if (frozenPaneRef.current) observer.observe(frozenPaneRef.current);
      if (scrollPaneRef.current) observer.observe(scrollPaneRef.current);
    }

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", handleResize);
      observer?.disconnect();
    };
  }, [allChannels, pageAggregatorSummaries, syncExplorerTableRowHeights]);

  const metricsParam = visibleChannels.join(",");
  const chart = useElementWidth<HTMLDivElement>(320);

  const aggregatesQuery = useTelemetryAggregates(
    {
      deviceId,
      start,
      end,
      bucketSeconds: 900,
      maxPoints: 4096,
      metrics: metricsParam,
      metricsKey: visibleChannels.slice().sort().join(","),
      moduleKey: "explorer",
    },
    Boolean(deviceId) && visibleChannels.length > 0,
  );
  const rawChartPoints = useMemo<TelemetryAggregatePointDto[]>(
    () =>
      pageRows
        .slice()
        .sort((a, b) => {
          const aTime = new Date(a.time).getTime();
          const bTime = new Date(b.time).getTime();
          if (Number.isFinite(aTime) && Number.isFinite(bTime)) {
            return aTime - bTime;
          }
          return a.time.localeCompare(b.time);
        })
        .map((row) => ({
          t: row.time,
          values: Object.fromEntries(
            visibleChannels.map((key) => [key, coerceTelemetryNumber(row.data?.[key])]),
          ),
        })),
    [pageRows, visibleChannels],
  );
  const aggregatePoints = aggregatesQuery.data?.points ?? [];
  const chartPoints = aggregatePoints.length > 0 ? aggregatePoints : rawChartPoints;
  const chartPointMode = aggregatePoints.length > 0 ? "aggregate" : "raw";
  const chartDataRevision = aggregatePoints.length > 0 ? aggregatesQuery.dataUpdatedAt : rowsQuery.dataUpdatedAt;
  const latestReadingRanges = useMemo(
    () =>
      Object.fromEntries(
        allChannels.map((channel) => {
          const values = metricValues(chartPoints, channel.key);
          return [
            channel.key,
            {
              min: values.length > 0 ? Math.min(...values) : null,
              max: values.length > 0 ? Math.max(...values) : null,
            },
          ];
        }),
      ) as Record<string, { min: number | null; max: number | null }>,
    [allChannels, chartPoints],
  );
  const normalizedChartPoints = useMemo(
    () => normalizeExplorerPoints(chartPoints, visibleChannels),
    [chartPoints, visibleChannels],
  );
  const comparisonChartPoints = normalizeSeries ? normalizedChartPoints : chartPoints;
  const heatmapPoints = useMemo(() => sampleExplorerPoints(chartPoints), [chartPoints]);
  const visibleChannelObjects = useMemo(
    () => visibleChannels.map((key) => numericChannels.find((channel) => channel.key === key)).filter((channel): channel is DeviceChannelDto => Boolean(channel)),
    [numericChannels, visibleChannels],
  );
  const exportChannelObjects = useMemo(
    () =>
      exportChannelKeys
        .map((key) => allChannels.find((channel) => channel.key === key))
        .filter((channel): channel is DeviceChannelDto => Boolean(channel)),
    [allChannels, exportChannelKeys],
  );
  const exportChannelOptions = useMemo(
    () =>
      allChannels.map((channel) => ({
        value: channel.key,
        label: channelLabels[channel.key] ?? channel.key,
        description: channel.unit || channel.key,
      })),
    [allChannels, channelLabels],
  );
  const activeExportChannels = useMemo(
    () => (exportScope === "all" ? allChannels : exportChannelObjects),
    [allChannels, exportChannelObjects, exportScope],
  );

  const deviceHealthModel = deviceQuery.data ? deviceActivityModelFromApi(deviceQuery.data) : null;

  const exportReadings = useCallback(async () => {
    if (totalRows === 0 || activeExportChannels.length === 0 || !exportFormat || !deviceId) {
      return;
    }
    const { blob, fileName } = await exportTelemetry({
      deviceId,
      format: exportFormat,
      timeRange: { start, end: effectiveEnd },
      workspaceScope: allWorkspaces ? "all" : null,
      moduleKey: "explorer",
      sortBy: "time",
      order: exportOrder,
      query: rowSearch.trim() || null,
      channels: activeExportChannels.map((channel) => channel.key),
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName ?? `explorer-${deviceId.slice(0, 8) || "device"}-readings.${exportFormat}`;
    anchor.click();
    URL.revokeObjectURL(url);
    setExportDialogOpen(false);
    setExportFormat(null);
    setExportDropdownValue("");
  }, [activeExportChannels, allWorkspaces, deviceId, effectiveEnd, end, exportFormat, exportOrder, rowSearch, start, totalRows]);

  const openExportDialog = useCallback((nextFormat: ExplorerExportFormat) => {
    setExportFormat(nextFormat);
    setExportScope("all");
    setExportOrder("desc");
    setExportDialogOpen(true);
  }, []);

  const closeExportDialog = useCallback(() => {
    setExportDialogOpen(false);
    setExportFormat(null);
    setExportDropdownValue("");
  }, []);

  const handleExportDropdownChange = useCallback((nextValue: string) => {
    setExportDropdownValue(nextValue);
    if (nextValue === "csv" || nextValue === "pdf") {
      openExportDialog(nextValue);
    }
  }, [openExportDialog]);

  const selectedDeviceLabel = deviceQuery.data?.name || deviceQuery.data?.device_uid || "No logger selected";
  const deviceStatusVariant = deviceHealthModel ? operationalStatusToIndicatorVariant(deviceHealthModel.status) : "neutral";
  const blockedMessage = entitlementBlockedMessage(rowsQuery.error, aggregatesQuery.error);
  const handleSortChange = useCallback((nextKey: ExplorerTableSortKey) => {
    setPage(1);
    setSortOrder((currentOrder) => {
      if (sortBy === nextKey) {
        return currentOrder === "asc" ? "desc" : "asc";
      }
      return nextKey === "time" ? "desc" : "asc";
    });
    setSortBy(nextKey);
  }, [sortBy]);

  // Body page-header content is intentionally omitted; device context stays in the sticky title.
  return (
    <EnterprisePageShell
      title={`Explorer - ${selectedDeviceLabel}`}
      titleHref={deviceId ? `/devices/${deviceId}` : undefined}
      hideHeaderSubtitle
      titleAdornment={
        deviceHealthModel ? (
          <span
            className={cn(
              "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-[0.12em]",
              deviceHealthModel.status === "ONLINE" && "border-success/35 bg-success/12 text-success",
              deviceHealthModel.status === "OFFLINE" && "border-border/70 bg-muted-bg/55 text-muted",
              deviceHealthModel.status === "STALE" && "border-warning/35 bg-warning/12 text-warning",
              deviceHealthModel.status === "ERROR" && "border-error/35 bg-error/12 text-error",
              deviceHealthModel.status === "MAINTENANCE" && "border-primary/15 bg-primary/10 text-primary",
              deviceHealthModel.status === "VISUALIZATION_DISABLED" && "border-warning/35 bg-warning/12 text-warning",
              deviceHealthModel.status === "INGESTION_DISABLED" && "border-error/35 bg-error/12 text-error",
            )}
            title={deviceHealthModel.delayLabel}
          >
            <StatusIndicator
              variant={deviceStatusVariant}
              size="sm"
              pulse={deviceHealthModel.status === "ONLINE"}
              showRing={deviceHealthModel.status === "STALE"}
            />
            {formatDeviceOperationalStatusLabel(deviceHealthModel.status)}
          </span>
        ) : null
      }
    >
        <div className="flex flex-col gap-ds-5">
        {devicesQuery.isLoading || (Boolean(deviceId) && (deviceQuery.isLoading || !deviceQuery.data)) ? (
          <Card className="py-ds-10 text-center text-caption text-muted" role="status">Loading channels...</Card>
        ) : (
          <>
        {blockedMessage ? (
          <Card className="border-warning/30 bg-warning/10 text-warning" role="status">
            {blockedMessage}
          </Card>
        ) : null}

        {disableState ? (
          <Card className={disableState.mode === "ingestion" ? "border-error/30 bg-error/10 text-error" : "border-warning/30 bg-warning/10 text-warning"} role="status">
            {disableState.mode === "ingestion"
              ? "This device has ingestion disabled. Explorer remains read-only for historical telemetry already stored in the database."
              : "This device has visualization disabled. Explorer stops at the disable timestamp, but all historical data before that point remains visible."}
          </Card>
        ) : null}

        {allChannels.length > 0 ? (
          <div className="min-w-0 overflow-hidden">
            <div className="scrollbar-hidden flex min-w-0 gap-ds-3 overflow-x-auto py-1">
              {allChannels.map((channel) => (
                <ExplorerLatestReadingCard
                  key={channel.id}
                  channel={channel}
                  value={latestReading?.data?.[channel.key]}
                  thresholds={channel.data_type === "number" ? channelThresholds[channel.key] ?? null : null}
                  range={latestReadingRanges[channel.key] ?? { min: null, max: null }}
                />
              ))}
            </div>
          </div>
        ) : null}

        {showGraph ? (
          <Card className="order-2 space-y-ds-4 p-ds-5">
          <div className="flex flex-wrap items-start justify-between gap-ds-3">
            <div className="space-y-1">
              <p className="text-title font-semibold text-foreground">All channel readings</p>
              <p className="text-caption text-muted">Switch views to compare channels, inspect distributions, or scan dense telemetry quickly.</p>
            </div>
            <div className="flex flex-wrap items-end justify-end gap-ds-3">
              <div className="mb-1 rounded-control border border-card-border bg-surface-secondary px-ds-3 py-1 text-micro font-semibold uppercase tracking-[0.12em] text-muted">
                {chartPoints.length} {chartPointMode} points
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-ds-3">
            <div className="flex flex-wrap items-center gap-ds-2">
              <span className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">View</span>
              <div className="segment-tabs bg-surface-secondary">
                {EXPLORER_VIEW_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setViewMode(option.value)}
                    className={cn("segment-tab", viewMode === option.value && "segment-tab-active bg-card")}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            {(viewMode === "overlay" || viewMode === "multiples") ? (
              <button
                type="button"
                onClick={() => setNormalizeSeries((current) => !current)}
                aria-pressed={normalizeSeries}
                className={cn(
                  "rounded-control border px-ds-3 py-1.5 text-caption font-semibold transition-colors",
                  normalizeSeries
                    ? "border-primary/25 bg-primary/10 text-primary"
                    : "border-card-border bg-surface-primary text-muted hover:text-foreground",
                )}
              >
                Normalize scale
              </button>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-ds-2">
            {numericChannels.map((channel: DeviceChannelDto) => {
              const visible = visibleChannels.includes(channel.key);
              return (
                <button
                  key={channel.id}
                  type="button"
                  onClick={() =>
                    setVisibleChannels((current) =>
                      visible ? current.filter((key) => key !== channel.key) : [...current, channel.key],
                    )
                  }
                  className={cn(
                    "inline-flex items-center gap-2 rounded-full border px-ds-3 py-1 text-caption transition-colors",
                    visible ? "border-primary/20 bg-primary/10 text-primary" : "border-card-border bg-surface-primary text-muted hover:text-foreground",
                  )}
                >
                  {!visible ? <EyeOff className="h-3.5 w-3.5" aria-hidden /> : null}
                  {channelLabels[channel.key] ?? channel.key}
                  {channel.unit ? <span className="text-micro uppercase tracking-[0.1em] opacity-70">{channel.unit}</span> : null}
                </button>
              );
            })}
          </div>

          {visibleChannels.length === 0 ? (
            <div className="rounded-card border border-dashed border-card-border bg-surface-secondary px-ds-4 py-ds-10 text-center text-caption text-muted" role="status">
              All channels are hidden. Click a channel chip to put it back on the chart.
            </div>
          ) : viewMode === "overlay" ? (
            <div ref={chart.ref} className="w-full min-w-0">
              <TelemetryUPlotChart
                width={chart.width}
                height={360}
                deviceId={deviceId}
                metricKeys={visibleChannels}
                seriesLabels={channelLabels}
                aggregatePoints={comparisonChartPoints}
                dataRevision={chartDataRevision}
                liveEnabled={false}
                liveWindowPoints={4096}
              />
            </div>
          ) : viewMode === "multiples" ? (
            <div className="grid gap-ds-3 lg:grid-cols-2">
              {visibleChannelObjects.map((channel) => (
                <ExplorerMiniChart
                  key={channel.key}
                  deviceId={deviceId}
                  metricKey={channel.key}
                  label={channelLabels[channel.key] ?? channel.key}
                  unit={normalizeSeries ? "normalized 0-100" : channel.unit ?? ""}
                  points={comparisonChartPoints}
                  dataRevision={chartDataRevision}
                />
              ))}
            </div>
          ) : viewMode === "heatmap" ? (
            <div className="overflow-x-auto rounded-card border border-card-border bg-surface-secondary p-ds-3">
              <div
                className="grid min-w-[42rem] gap-[2px]"
                style={{
                  gridTemplateColumns: `10rem repeat(${Math.max(1, heatmapPoints.length)}, minmax(0,1fr))`,
                }}
              >
                <div className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">Channel</div>
                {heatmapPoints.map((point, index) => (
                  <div key={`${point.t}-${index}`} className="h-5 text-center text-[0.58rem] text-muted">
                    {index % Math.max(1, Math.ceil(heatmapPoints.length / 8)) === 0 ? formatExplorerDate(point.t).split(",")[1]?.trim() : ""}
                  </div>
                ))}
                {visibleChannelObjects.map((channel) => {
                  const values = metricValues(chartPoints, channel.key);
                  const min = values.length ? Math.min(...values) : 0;
                  const max = values.length ? Math.max(...values) : 0;
                  return (
                    <Fragment key={channel.key}>
                      <div className="truncate pr-ds-2 text-caption font-semibold text-foreground" title={channelLabels[channel.key] ?? channel.key}>
                        {channelLabels[channel.key] ?? channel.key}
                      </div>
                      {heatmapPoints.map((point, index) => {
                        const value = point.values[channel.key];
                        return (
                          <div
                            key={`${channel.key}-${point.t}-${index}`}
                            className="h-6 rounded-[3px] border border-border/50 dark:border-white/10"
                            style={{ background: heatmapCellColor(value, min, max) }}
                            title={`${channelLabels[channel.key] ?? channel.key}: ${formatTelemetryValue(value)}${channel.unit ? ` ${channel.unit}` : ""} at ${formatExplorerDate(point.t)}`}
                          />
                        );
                      })}
                    </Fragment>
                  );
                })}
              </div>
              <div className="mt-ds-3 text-caption text-muted">Color intensity is normalized per channel so mixed units remain comparable.</div>
            </div>
          ) : (
            <div className="grid gap-ds-3 lg:grid-cols-2">
              {visibleChannelObjects.map((channel) => (
                <ExplorerHistogramCard
                  key={channel.key}
                  channel={channel}
                  label={channelLabels[channel.key] ?? channel.key}
                  points={chartPoints}
                />
              ))}
            </div>
          )}
          </Card>
        ) : null}

        <Card className="table-shell order-1 overflow-hidden border-[color:var(--table-border)] bg-[var(--bg-surface)] p-0">
          <div className="flex flex-col gap-ds-3 border-b border-[color:var(--table-border)] px-ds-4 py-ds-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="min-w-0 space-y-1">
              <p className="text-title font-semibold text-foreground">Channel activity table</p>
              {/* <p className="text-caption text-muted">Paginated raw readings with one column per device channel, ready for export.</p> */}
            </div>
            <div className="flex flex-wrap items-end gap-ds-2 xl:flex-nowrap xl:justify-end">
              <button
                type="button"
                onClick={() => setShowGraph((current) => !current)}
                aria-pressed={showGraph}
                className={cn(
                  "ui-control h-9 shrink-0 whitespace-nowrap rounded-control px-3 text-caption font-semibold transition-colors",
                  showGraph
                    ? "border-primary/25 bg-primary/10 text-primary"
                    : "border-card-border bg-card text-muted hover:border-primary/20 hover:text-foreground",
                )}
              >
                {showGraph ? "Hide graph" : "Show graph"}
              </button>
              <InlineTimeRangeControls showLabel={false} bare />
              <label className="flex min-w-[12rem] flex-1 md:min-w-[14rem] md:max-w-[14rem] xl:flex-none">
                <div className="ui-control flex items-center gap-2.5 rounded-control px-3 focus-within:border-primary focus-within:ring-2 focus-within:ring-ring/25">
                  <Search className="pointer-events-none h-4 w-4 shrink-0 text-muted/80" aria-hidden />
                  <input
                    value={rowSearch}
                    onChange={(e) => setRowSearch(e.target.value)}
                    placeholder="Filter table values"
                    className="min-w-0 flex-1 border-0 bg-transparent p-0 text-caption text-foreground outline-none placeholder:text-muted/75"
                  />
                </div>
              </label>
              <div className="flex w-[8.5rem] shrink-0">
                <ThemedSelect
                  value={exportDropdownValue}
                  ariaLabel="Export data"
                  disabled={totalRows === 0 || allChannels.length === 0}
                  triggerClassName="h-9 rounded-control border border-card-border bg-surface-primary px-3 text-caption font-medium shadow-none hover:border-primary/20 focus-visible:ring-primary/20"
                  menuClassName="rounded-2xl border-card-border p-ds-2 shadow-card"
                  optionClassName="rounded-control"
                  options={[
                    { value: "", label: "Export" },
                    { value: "csv", label: "CSV" },
                    { value: "pdf", label: "PDF" },
                  ]}
                  onChange={handleExportDropdownChange}
                />
              </div>
            </div>
          </div>

          <div className="grid" style={{ gridTemplateColumns: `${EXPLORER_DATE_COLUMN_WIDTH} minmax(0,1fr)` }}>
            <div
              ref={frozenPaneRef}
              className="table-pane-shadow relative z-[2] overflow-hidden border-r border-[color:var(--table-border)] bg-[var(--bg-surface)]"
            >
              <table className="table-fixed border-collapse text-left" style={{ width: EXPLORER_DATE_COLUMN_WIDTH, minWidth: EXPLORER_DATE_COLUMN_WIDTH }}>
                <thead>
                  <tr
                    ref={(element) => {
                      leftRowRefs.current["header-main"] = element;
                    }}
                    className="table-header-row"
                  >
                    <th className="border-b border-[color:var(--table-border)] bg-[var(--table-header-bg)] px-ds-4 py-ds-3 text-micro font-semibold uppercase tracking-[0.12em] text-muted">
                      <button
                        type="button"
                        onClick={() => handleSortChange("time")}
                        className="inline-flex items-center gap-1.5 text-left transition-colors hover:text-foreground"
                      >
                        <span>Date</span>
                        {sortBy === "time" ? (
                          sortOrder === "asc" ? <ArrowUp className="h-3.5 w-3.5" aria-hidden /> : <ArrowDown className="h-3.5 w-3.5" aria-hidden />
                        ) : (
                          <ArrowUpDown className="h-3.5 w-3.5 opacity-65" aria-hidden />
                        )}
                      </button>
                    </th>
                  </tr>
                  <tr
                    ref={(element) => {
                      leftRowRefs.current["header-aggregators"] = element;
                    }}
                    className="bg-muted-bg/15 align-top"
                  >
                    <th className="border-b border-[color:var(--table-border)] bg-muted-bg/15 px-ds-4 py-ds-2 text-micro font-normal uppercase tracking-[0.12em] text-muted">
                      Page aggregators
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row, index) => (
                    <tr
                      key={`date-${row.device_id}-${row.time}-${pageStart + index}`}
                      ref={(element) => {
                        leftRowRefs.current[`row-${index}`] = element;
                      }}
                      className="table-data-row group odd:bg-muted-bg/20 even:bg-[var(--bg-surface)] dark:odd:bg-white/[0.03]"
                    >
                      <td className={cn("whitespace-nowrap border-b border-[color:var(--table-border)] px-ds-4 py-ds-3 text-caption text-foreground transition-colors group-hover:bg-[var(--table-row-hover)]", index % 2 === 0 ? "bg-muted-bg/20 dark:bg-white/[0.03]" : "bg-[var(--bg-surface)]")}>
                        {formatExplorerDate(row.time)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div ref={scrollPaneRef} className="overflow-x-auto">
              <table className="min-w-full border-collapse text-left">
                <thead className="bg-[var(--table-header-bg)]">
                  <tr
                    ref={(element) => {
                      rightRowRefs.current["header-main"] = element;
                    }}
                    className="table-header-row"
                  >
                    {allChannels.map((channel) => (
                      <th key={channel.id} className="whitespace-nowrap border-b border-[color:var(--table-border)] px-ds-4 py-ds-3 text-micro font-semibold uppercase tracking-[0.12em] text-muted">
                        <button
                          type="button"
                          onClick={() => handleSortChange(channel.key)}
                          className="inline-flex items-center gap-1.5 text-left transition-colors hover:text-foreground"
                        >
                          <span>{channelLabels[channel.key] ?? channel.key}</span>
                          {sortBy === channel.key ? (
                            sortOrder === "asc" ? <ArrowUp className="h-3.5 w-3.5" aria-hidden /> : <ArrowDown className="h-3.5 w-3.5" aria-hidden />
                          ) : (
                            <ArrowUpDown className="h-3.5 w-3.5 opacity-65" aria-hidden />
                          )}
                        </button>
                      </th>
                    ))}
                  </tr>
                  <tr
                    ref={(element) => {
                      rightRowRefs.current["header-aggregators"] = element;
                    }}
                    className="bg-muted-bg/15 align-top"
                  >
                    {allChannels.map((channel) => (
                      <th key={`${channel.id}-aggregators`} className="border-b border-[color:var(--table-border)] px-ds-4 py-ds-2 font-normal">
                        <AggregatorSummaryCell summary={pageAggregatorSummaries[channel.key] ?? ""} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row, index) => (
                    <tr
                      key={`${row.device_id}-${row.time}-${pageStart + index}`}
                      ref={(element) => {
                        rightRowRefs.current[`row-${index}`] = element;
                      }}
                      className="table-data-row group odd:bg-muted-bg/20 even:bg-[var(--bg-surface)] dark:odd:bg-white/[0.03]"
                    >
                      {allChannels.map((channel) => (
                        <TelemetryTableValueCell
                          key={channel.id}
                          value={row.data?.[channel.key]}
                          unit={channel.unit}
                          thresholds={channelThresholds[channel.key]}
                        />
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {pageRows.length === 0 ? (
            <div className="px-ds-4 py-ds-10 text-center text-caption text-muted">
              {deviceId ? "No telemetry rows found in this range." : "Choose a device to start exploring."}
            </div>
          ) : null}

          <div className="grid gap-ds-3 border-t border-[color:var(--table-border)] px-ds-4 py-ds-3 md:grid-cols-[1fr_auto_1fr] md:items-center">
            <div className="flex flex-wrap items-center gap-ds-3 md:justify-self-start">
              <p className="text-caption text-muted">Page {safePage} of {totalPages}</p>
              <label className="flex items-center gap-ds-2 text-caption text-muted">
                <span>Rows per page</span>
                <ThemedSelect
                  value={String(pageSize)}
                  ariaLabel="Rows per page"
                  triggerClassName="h-9 min-w-[5rem] rounded-control border border-card-border bg-surface-primary px-3 text-caption font-medium shadow-none hover:border-primary/20 focus-visible:ring-primary/20"
                  menuClassName="rounded-2xl border-card-border p-ds-2 shadow-card"
                  optionClassName="rounded-control"
                  options={PAGE_SIZE_OPTIONS.map((option) => ({ value: String(option), label: String(option) }))}
                  onChange={(nextValue) => setPageSize(Number(nextValue) as (typeof PAGE_SIZE_OPTIONS)[number])}
                />
              </label>
            </div>
            <p className="text-center text-caption text-muted">
              {pageSummary}
            </p>
            <div className="flex flex-wrap gap-ds-2 md:justify-self-end">
              <button
                type="button"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={safePage <= 1}
                className="btn btn-ghost btn-sm disabled:opacity-40"
              >
                Previous
              </button>
              {buildPageNumbers(safePage, totalPages).map((value, index) =>
                value === "ellipsis" ? (
                  <span key={`ellipsis-${index}`} className="px-ds-2 py-ds-1 text-caption text-muted">
                    ...
                  </span>
                ) : (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setPage(value)}
                    className={cn(
                      "min-w-[2.25rem] rounded-lg px-ds-2 py-ds-1 text-caption font-semibold transition-colors",
                      value === safePage
                        ? "border border-[var(--btn-primary-to)] bg-[var(--btn-primary-to)] text-[var(--btn-primary-fg)] shadow-sm"
                        : "border border-card-border bg-surface-primary text-muted hover:text-foreground",
                    )}
                  >
                    {value}
                  </button>
                ),
              )}
              <button
                type="button"
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                disabled={safePage >= totalPages}
                className="btn btn-ghost btn-sm disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </Card>
          </>
        )}
      </div>
      <Modal
        open={exportDialogOpen}
        onClose={closeExportDialog}
        title={`Export ${exportFormat?.toUpperCase() ?? ""}`}
        description="Choose whether to export all visible table channels or only a selected subset."
        size="md"
        footer={
          <div className="flex items-center justify-end gap-ds-2">
            <Button type="button" variant="ghost" size="sm" onClick={closeExportDialog}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={exportReadings}
              disabled={totalRows === 0 || activeExportChannels.length === 0 || !exportFormat}
            >
              Export {exportFormat?.toUpperCase() ?? ""}
            </Button>
          </div>
        }
      >
        <div className="grid gap-ds-4">
          <div className="grid gap-ds-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setExportScope("all")}
              className={cn(
                "rounded-card border px-ds-4 py-ds-3 text-left transition-colors",
                exportScope === "all"
                  ? "border-accent bg-accent text-accent-foreground"
                  : "border-card-border bg-surface-primary text-muted hover:border-primary/25 hover:text-foreground",
              )}
            >
              <p className="text-caption font-semibold">All channels</p>
              <p className={cn("mt-1 text-micro", exportScope === "all" ? "text-accent-foreground/85" : "text-muted")}>
                {allChannels.length} channel{allChannels.length === 1 ? "" : "s"} from the current table view.
              </p>
            </button>
            <button
              type="button"
              onClick={() => setExportScope("selected")}
              className={cn(
                "rounded-card border px-ds-4 py-ds-3 text-left transition-colors",
                exportScope === "selected"
                  ? "border-accent bg-accent text-accent-foreground"
                  : "border-card-border bg-surface-primary text-muted hover:border-primary/25 hover:text-foreground",
              )}
            >
              <p className="text-caption font-semibold">Select channels</p>
              <p className={cn("mt-1 text-micro", exportScope === "selected" ? "text-accent-foreground/85" : "text-muted")}>
                Export only the channels you choose below.
              </p>
            </button>
          </div>
          {exportScope === "selected" ? (
            <div className="grid gap-ds-2">
              <span className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">Channels</span>
              <ThemedMultiSelect
                value={exportChannelKeys}
                onChange={setExportChannelKeys}
                options={exportChannelOptions}
                placeholder="Select channels"
                searchPlaceholder="Search channels"
                ariaLabel="Channels to export"
                maxSelectedLabels={2}
                triggerClassName="min-h-11 rounded-xl border border-card-border bg-card px-3 text-caption font-medium shadow-none hover:border-primary/20 focus-visible:ring-primary/20"
                menuClassName="rounded-2xl border-card-border p-ds-2 shadow-card"
                optionClassName="rounded-xl"
              />
              <p className="text-caption text-muted">
                {exportChannelKeys.length === 0
                  ? "Select at least one channel to continue."
                  : `${exportChannelKeys.length} channel${exportChannelKeys.length === 1 ? "" : "s"} selected.`}
              </p>
            </div>
          ) : null}
          <div className="grid gap-ds-2">
            <span className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">Row order</span>
            <div className="grid gap-ds-2 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setExportOrder("desc")}
                className={cn(
                "rounded-card border px-ds-4 py-ds-3 text-left transition-colors",
                  exportOrder === "desc"
                    ? "border-accent bg-accent text-accent-foreground"
                    : "border-card-border bg-surface-primary text-muted hover:border-primary/25 hover:text-foreground",
                )}
              >
                <p className="text-caption font-semibold">Latest first</p>
                <p className={cn("mt-1 text-micro", exportOrder === "desc" ? "text-accent-foreground/85" : "text-muted")}>
                  Descending by reading time.
                </p>
              </button>
              <button
                type="button"
                onClick={() => setExportOrder("asc")}
                className={cn(
                "rounded-card border px-ds-4 py-ds-3 text-left transition-colors",
                  exportOrder === "asc"
                    ? "border-accent bg-accent text-accent-foreground"
                    : "border-card-border bg-surface-primary text-muted hover:border-primary/25 hover:text-foreground",
                )}
              >
                <p className="text-caption font-semibold">Oldest first</p>
                <p className={cn("mt-1 text-micro", exportOrder === "asc" ? "text-accent-foreground/85" : "text-muted")}>
                  Ascending by reading time.
                </p>
              </button>
            </div>
          </div>
          <div className="rounded-control border border-card-border bg-surface-secondary px-ds-4 py-ds-3 text-caption text-muted">
            {totalRows} filtered row{totalRows === 1 ? "" : "s"} will be exported in {exportFormat?.toUpperCase() ?? "this format"} with {exportOrder === "asc" ? "oldest-first" : "latest-first"} ordering.
          </div>
        </div>
      </Modal>
    </EnterprisePageShell>
  );
}

function buildPageNumbers(current: number, total: number): Array<number | "ellipsis"> {
  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }

  if (current <= 4) {
    return [1, 2, 3, 4, 5, "ellipsis", total];
  }

  if (current >= total - 3) {
    return [1, "ellipsis", total - 4, total - 3, total - 2, total - 1, total];
  }

  return [1, "ellipsis", current - 1, current, current + 1, "ellipsis", total];
}

function normalizeAggregators(values: string[] | null | undefined): AggregatorKey[] {
  const normalized = (values ?? []).filter((value): value is AggregatorKey =>
    value === "avg" || value === "sum" || value === "min" || value === "max" || value === "last" || value === "count",
  );
  const seen = new Set<AggregatorKey>();
  return normalized.filter((value) => {
    if (seen.has(value)) return false;
    seen.add(value);
    return true;
  });
}

function computeChannelAggregators(
  rows: TelemetryDto[],
  channelKey: string,
  aggregators: AggregatorKey[],
): Partial<Record<AggregatorKey, number | null>> {
  const numericValues = rows
    .map((row) => row.data?.[channelKey])
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value));

  const count = numericValues.length;
  const sum = count > 0 ? numericValues.reduce((acc, value) => acc + value, 0) : null;
  const avg = count > 0 && sum != null ? sum / count : null;
  const min = count > 0 ? Math.min(...numericValues) : null;
  const max = count > 0 ? Math.max(...numericValues) : null;
  const last = count > 0 ? (numericValues[0] ?? null) : null;

  const source: Record<AggregatorKey, number | null> = {
    avg,
    sum,
    min,
    max,
    last,
    count,
  };

  return Object.fromEntries(aggregators.map((key) => [key, source[key]])) as Partial<Record<AggregatorKey, number | null>>;
}

function formatValueWithUnit(value: number, unit: string | null | undefined, aggregator: AggregatorKey): string {
  if (aggregator === "count") {
    return formatTelemetryValue(value);
  }
  return `${formatTelemetryValue(value)}${unit ? ` ${unit}` : ""}`;
}

function AggregatorSummaryCell({ summary }: { summary: string }) {
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);

  const updateTooltipPosition = useCallback(() => {
    const element = triggerRef.current;
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const margin = 12;
    const tooltipWidth = tooltipRef.current?.offsetWidth ?? Math.min(window.innerWidth - margin * 2, 320);
    const centeredLeft = rect.left + rect.width / 2 - tooltipWidth / 2;
    const clampedLeft = Math.min(Math.max(centeredLeft, margin), Math.max(margin, window.innerWidth - tooltipWidth - margin));
    setTooltipPosition({
      top: rect.bottom + 8,
      left: clampedLeft,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const frame = window.requestAnimationFrame(updateTooltipPosition);
    window.addEventListener("scroll", updateTooltipPosition, true);
    window.addEventListener("resize", updateTooltipPosition);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", updateTooltipPosition, true);
      window.removeEventListener("resize", updateTooltipPosition);
    };
  }, [isOpen, updateTooltipPosition]);

  if (!summary) {
    return <span className="block text-caption text-muted">--</span>;
  }

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        className="block max-w-[13rem] cursor-help truncate text-caption font-normal text-foreground/85"
        tabIndex={0}
      >
        {summary}
      </span>
      {isOpen && tooltipPosition
        ? createPortal(
            <div
              ref={tooltipRef}
              className="pointer-events-none fixed z-[1000] w-max max-w-[min(24rem,calc(100vw-1.5rem))] rounded-card border border-card-border bg-surface-elevated px-3 py-2 text-left text-caption leading-5 text-foreground shadow-overlay"
              style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
            >
              {summary}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function TelemetryTableValueCell({
  value,
  unit,
  thresholds,
}: {
  value: unknown;
  unit: string | null | undefined;
  thresholds: ChannelThresholds | undefined;
}) {
  const severity = telemetryThresholdSeverity(value, thresholds);
  return (
    <td
      className={cn(
        "whitespace-nowrap border-b border-[color:var(--table-border)] px-ds-4 py-ds-3 text-caption transition-colors group-hover:bg-[var(--table-row-hover)]",
        severity ? telemetrySeverityTextClass(severity) : "text-foreground/80",
      )}
    >
      <span>{formatTelemetryValue(value)}</span>
      {unit ? <span className="ml-1 opacity-70">{unit}</span> : null}
    </td>
  );
}
